import type { ReactNode } from 'react';

import {
  VENDOR_ADDRESS_FIELDS,
  VENDOR_IMPORT_INDIVIDUAL_REASON_MAX,
  vendorImportTileOf,
  type VendorImportLinkCandidate,
  type VendorImportPlanRow,
} from '@clm/shared-types';

import { DataTable, type DataTableColumn } from '../../components/DataTable';
import {
  ADDRESS_FIELD_LABEL,
  ALIAS_COLUMN_COPY,
  ALIAS_STATUS_COPY,
  CANDIDATE_CONFIRMS_PENDING_COPY,
  FLAG_COPY,
  FLAG_NEEDS_ATTENTION,
  GAP_COPY,
  INDIVIDUAL_DEFAULT_COPY,
  INDIVIDUAL_INCLUDE_COPY,
  LINK_MATCH_COPY,
  OMIT_ALIAS_COPY,
  REASON_COPY,
  SHARED_ALIAS_COPY,
  TILE_COPY,
  rowLabel,
} from '../vendorImportCopy';
import {
  candidateChoice,
  choicesFor,
  gapsOf,
  isDecidable,
  isIncludedIndividual,
  needsShortConfirmation,
  omittableAliases,
  sharedRequirement,
  shortNamesOf,
  showsChoice,
  withChoice,
  withInclude,
  type ChoiceOption,
  type Decisions,
  type RowChoice,
  type RowDecision,
} from './decisions';

/**
 * The vendor-import plan table — CLM-113 (§4.7 "The plan table").
 *
 * One row per data row of the sheet: its row reference and vendor number, what
 * the row would write (name, aliases with their status, address), what the
 * plan proposes and why (reasons and flags as text and an icon, never colour
 * alone), and the Admin's controls. Every control is labelled with the row's
 * reference, so a screen reader hears which row it is acting on.
 *
 * ## Never a person's name
 *
 * An individual's plan row carries no name, alias or address (R-113-5); this
 * table also never prints a candidate's name on that row, because a candidate
 * matched by name would spell the person's name out. A same-PAN candidate says
 * "Same PAN as row *n*" — the page is never sent a PAN to show (R-113-26).
 */

export interface PlanTableProps {
  readonly rows: readonly VendorImportPlanRow[];
  readonly decisions: Decisions;
  /** Executing or executed: the decisions are shown, not editable. */
  readonly readOnly: boolean;
  /** An execute is in flight, so the rows on screen may be about to change. */
  readonly busy: boolean;
  /** Per-row refusals from the last execute (`422` at `approved[i]`), by `row_ref`. */
  readonly rowErrors: Readonly<Record<string, string>>;
  readonly onDecide: (rowRef: string, next: RowDecision) => void;
  readonly empty: ReactNode;
}

function addressLine(row: VendorImportPlanRow): string | null {
  if (row.address === null) return null;
  const address = row.address;
  const parts = VENDOR_ADDRESS_FIELDS.map((field) => address[field]).filter((part): part is string => part !== null);
  return parts.length === 0 ? null : parts.join(', ');
}

function candidateLabel(candidate: VendorImportLinkCandidate, individual: boolean): string {
  const inactive = candidate.is_active ? '' : ' (inactive)';
  if (candidate.row_ref !== null) {
    const lead = candidate.matched_on === 'sheet_row_pan' ? 'Same PAN as' : 'Same vendor as';
    const key = candidate.party_key === null ? '' : ` (${candidate.party_key})`;
    return individual || candidate.display_name === null
      ? `${lead} ${rowLabel(candidate.row_ref)}${key}`
      : `${lead} ${rowLabel(candidate.row_ref)}${key} — ${candidate.display_name}${inactive}`;
  }
  return individual || candidate.display_name === null
    ? `Same vendor as ${candidate.party_key ?? ''}${inactive}`
    : `Same vendor as ${candidate.display_name} (${candidate.party_key ?? ''})${inactive}`;
}

function optionLabel(option: ChoiceOption, row: VendorImportPlanRow): string {
  switch (option.kind) {
    case 'link':
      return candidateLabel(option.candidate, row.is_individual);
    case 'update':
      return row.flags.includes('name_changed') ? 'Same vendor, renamed' : 'Update this vendor';
    case 'create':
      return 'A new vendor';
    case 'skip':
    default:
      return 'Skip';
  }
}

function VendorCell({ row }: { readonly row: VendorImportPlanRow }) {
  const existing = row.existing_party;
  const address = addressLine(row);
  if (row.is_individual) {
    return (
      <>
        <span className="mut">An individual — not shown</span>
        {existing === null ? null : (
          <span className="vi-sub">
            Vendor <code>{existing.party_key}</code>
          </span>
        )}
      </>
    );
  }
  return (
    <>
      {row.flags.includes('name_changed') && existing !== null ? (
        <span className="vi-renamed">
          <span>
            <span className="mut">In CLM: </span>
            {existing.display_name}
          </span>
          <span aria-hidden="true"> → </span>
          <span>
            <span className="mut">In the sheet: </span>
            <strong>{row.display_name ?? '—'}</strong>
          </span>
        </span>
      ) : (
        <strong>{row.display_name ?? <span className="mut">No name</span>}</strong>
      )}
      {existing !== null ? (
        <span className="vi-sub">
          Vendor <code>{existing.party_key}</code>
          {existing.is_active ? null : <span className="tag out">inactive</span>}
        </span>
      ) : null}
      {row.key_preview !== null ? (
        <span className="vi-sub mut">
          New key <code>{row.key_preview}</code> (provisional — minted on import)
        </span>
      ) : null}
      {row.aliases !== null && row.aliases.length > 0 ? (
        <ul className="vi-aliases" aria-label={`Names on ${rowLabel(row.row_ref)}`}>
          {row.aliases.map((alias) => (
            <li key={`${alias.column}-${alias.text}`}>
              <span className="tag">
                {ALIAS_COLUMN_COPY[alias.column]}: {alias.text}
              </span>{' '}
              <span className="mut">
                {ALIAS_STATUS_COPY[alias.status]}
                {alias.other_party_keys.length + alias.other_row_refs.length > 0
                  ? ` (${[...alias.other_party_keys, ...alias.other_row_refs.map(rowLabel)].join(', ')})`
                  : ''}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
      {address === null ? null : <span className="vi-sub">{address}</span>}
      {row.address_changes.length > 0 ? (
        <span className="vi-sub mut">
          Changes: {row.address_changes.map((field) => ADDRESS_FIELD_LABEL[field]).join(', ')}
        </span>
      ) : null}
    </>
  );
}

function PlanCell({ row }: { readonly row: VendorImportPlanRow }) {
  const tile = vendorImportTileOf(row);
  /* Client and Partner matches are information: a company that is both is two parties (TD-02), never a link target. */
  const others = row.link_candidates.filter((candidate) => candidateChoice(candidate) === null);
  return (
    <>
      <strong className="vi-tile" data-tile={tile}>
        {TILE_COPY[tile]}
      </strong>
      {row.reasons.map((reason) => (
        <p key={reason} className="vi-reason">
          {REASON_COPY[reason]}
        </p>
      ))}
      {row.flags.length > 0 ? (
        <ul className="bundle-flags" aria-label={`Flags on ${rowLabel(row.row_ref)}`}>
          {row.flags.map((flag) => (
            <li key={flag} data-flag={flag}>
              <span aria-hidden="true">{FLAG_NEEDS_ATTENTION[flag] ? '⚠' : 'ℹ'}</span> {FLAG_COPY[flag]}
            </li>
          ))}
        </ul>
      ) : null}
      {others.map((candidate) => (
        <p key={`${candidate.party_type}-${candidate.party_key ?? ''}`} className="vi-sub mut">
          Also matches {candidate.party_type}{' '}
          {row.is_individual ? (
            <code>{candidate.party_key}</code>
          ) : (
            <>
              {candidate.display_name} (<code>{candidate.party_key}</code>)
            </>
          )}{' '}
          — {LINK_MATCH_COPY[candidate.matched_on]}. A company that is both a client and a vendor is two parties;
          only a vendor can be joined.
        </p>
      ))}
      {row.link_candidates
        .filter((candidate) => candidate.confirms_pending_alias && candidate.party_key !== null)
        .map((candidate) => (
          <p key={`pending-${candidate.party_key ?? ''}`} className="vi-sub mut" data-testid="vi-candidate-pending">
            <code>{candidate.party_key}</code>: {CANDIDATE_CONFIRMS_PENDING_COPY}
          </p>
        ))}
      {row.affects_parties_with_contracts || row.affected_contracts_in_scope > 0 ? (
        <p className="vi-sub mut" data-testid="vi-row-fr29">
          {row.affected_contracts_in_scope} {row.affected_contracts_in_scope === 1 ? 'contract' : 'contracts'} in your
          scope carry this vendor’s names{row.affects_parties_with_contracts ? ', and contracts outside your scope may too' : ''}:
          a name added here changes their FR-29 map, and the receipt gives the operator steps.
        </p>
      ) : null}
    </>
  );
}

function DecisionCell({
  row,
  decision,
  decisions,
  readOnly,
  error,
  onDecide,
}: {
  readonly row: VendorImportPlanRow;
  readonly decision: RowDecision | undefined;
  readonly decisions: Decisions;
  readonly readOnly: boolean;
  readonly error: string | undefined;
  readonly onDecide: (next: RowDecision) => void;
}) {
  const tile = vendorImportTileOf(row);
  if (!isDecidable(row) || decision === undefined) {
    return <span className="mut">{tile === 'unchanged' ? 'Nothing to write' : '—'}</span>;
  }
  const label = rowLabel(row.row_ref);
  const options = choicesFor(row);
  const shortNames = shortNamesOf(row);
  const omittable = omittableAliases(row);
  const shared = sharedRequirement(row, decision, decisions);
  const gaps = gapsOf(row, decision, decisions);
  const includeLabel = row.is_individual ? 'Include this person' : 'Import this row';

  return (
    <div className="vi-decide">
      <label className="vi-check">
        <input
          type="checkbox"
          aria-label={`${includeLabel} (${label})`}
          checked={decision.include}
          disabled={readOnly}
          onChange={(event) => onDecide(withInclude(decision, event.target.checked))}
        />{' '}
        {includeLabel}
      </label>

      {showsChoice(row) ? (
        <select
          aria-label={`What ${label} is`}
          value={decision.choice}
          disabled={readOnly}
          onChange={(event) => onDecide(withChoice(decision, event.target.value as RowChoice))}
        >
          {decision.choice === '' ? <option value="">Choose…</option> : null}
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {optionLabel(option, row)}
            </option>
          ))}
        </select>
      ) : null}

      {row.is_individual ? <p className="vi-sub mut">{INDIVIDUAL_DEFAULT_COPY}</p> : null}

      {decision.include && row.is_individual ? (
        <div className="field">
          <label htmlFor={`vi-reason-${row.row_ref}`}>
            {isIncludedIndividual(row)
              ? 'A new reason, to replace the one recorded when this person was included (optional)'
              : 'Reason for including this person'}
          </label>
          <textarea
            id={`vi-reason-${row.row_ref}`}
            aria-label={
              isIncludedIndividual(row)
                ? `New reason for the person on ${label} (optional)`
                : `Reason for including the person on ${label}`
            }
            aria-required={isIncludedIndividual(row) ? 'false' : 'true'}
            maxLength={VENDOR_IMPORT_INDIVIDUAL_REASON_MAX}
            placeholder="A contract with this person exists or is expected…"
            value={decision.individual_reason}
            disabled={readOnly}
            onChange={(event) => onDecide({ ...decision, individual_reason: event.target.value })}
          />
          <p className="vi-sub mut">{INDIVIDUAL_INCLUDE_COPY}</p>
        </div>
      ) : null}

      {decision.include && needsShortConfirmation(row, decision) ? (
        <label className="vi-check">
          <input
            type="checkbox"
            aria-label={`Confirm the short name on ${label}`}
            checked={decision.confirm_short_names}
            disabled={readOnly}
            onChange={(event) => onDecide({ ...decision, confirm_short_names: event.target.checked })}
          />{' '}
          {shortNames !== null && shortNames.length > 0
            ? `Confirm this short name: ${shortNames.join(', ')}`
            : row.is_individual
              ? 'Confirm this person’s short name'
              : 'Confirm the short name this row writes'}
        </label>
      ) : null}

      {decision.include
        ? omittable.map((alias) => (
            <label key={alias.column} className="vi-check">
              <input
                type="checkbox"
                aria-label={`Leave out ${ALIAS_COLUMN_COPY[alias.column]} “${alias.text}” on ${label}`}
                checked={decision.omit_aliases.includes(alias.column as 'name_2' | 'search_name')}
                disabled={readOnly}
                onChange={(event) => {
                  const column = alias.column as 'name_2' | 'search_name';
                  const rest = decision.omit_aliases.filter((entry) => entry !== column);
                  onDecide({ ...decision, omit_aliases: event.target.checked ? [...rest, column] : rest });
                }}
              />{' '}
              Leave out this alias: {alias.text} <span className="mut">— {OMIT_ALIAS_COPY}</span>
            </label>
          ))
        : null}

      {decision.include && shared.kind === 'confirm' ? (
        <label className="vi-check">
          <input
            type="checkbox"
            aria-label={`Keep the shared name on both parties (${label})`}
            checked={decision.accept_shared_aliases}
            disabled={readOnly}
            onChange={(event) => onDecide({ ...decision, accept_shared_aliases: event.target.checked })}
          />{' '}
          Keep this name on both parties{shared.names.length > 0 ? `: ${shared.names.join(', ')}` : ''}.{' '}
          <span className="mut">{SHARED_ALIAS_COPY}</span>
        </label>
      ) : null}
      {decision.include && shared.kind === 'auto' ? (
        <p className="vi-sub" role="note">
          A new vendor keeps the matched name, so both parties will hold it. {SHARED_ALIAS_COPY}
        </p>
      ) : null}

      {gaps.length > 0 ? (
        <ul className="vi-gaps" aria-label={`Still needed on ${label}`}>
          {gaps.map((gap) => (
            <li key={gap}>
              <span aria-hidden="true">⚠</span> {GAP_COPY[gap]}
            </li>
          ))}
        </ul>
      ) : null}
      {error === undefined ? null : (
        <p className="ferr" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

export function PlanTable({ rows, decisions, readOnly, busy, rowErrors, onDecide, empty }: PlanTableProps) {
  const columns: readonly DataTableColumn<VendorImportPlanRow>[] = [
    {
      id: 'row',
      header: 'Row',
      cell: (row) => (
        <>
          <span className="vi-ref">{rowLabel(row.row_ref)}</span>
          <span className="vi-sub">{row.vendor_no === null ? <span className="mut">no vendor no.</span> : <code>{row.vendor_no}</code>}</span>
        </>
      ),
    },
    { id: 'vendor', header: 'Vendor', cell: (row) => <VendorCell row={row} /> },
    { id: 'plan', header: 'Plan', cell: (row) => <PlanCell row={row} /> },
    {
      id: 'decision',
      header: 'Your decision',
      cell: (row) => (
        <DecisionCell
          row={row}
          decision={decisions[row.row_ref]}
          decisions={decisions}
          readOnly={readOnly}
          error={rowErrors[row.row_ref]}
          onDecide={(next) => onDecide(row.row_ref, next)}
        />
      ),
    },
  ];
  return (
    <DataTable
      caption="Rows in this sheet and what importing each would do"
      columns={columns}
      rows={rows}
      rowKey={(row) => row.row_ref}
      busy={busy}
      empty={empty}
    />
  );
}
